import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import Toast from "../../common/Toast/Toast";
import CfLoader from "../../common/CfLoader/CfLoader";
import EntityConstants from "../../configs/EntityConstants";
import VerificationApprovalModal from "../../common/VerificationApprovalModal/VerificationApprovalModal";
import VerificationApprovalCertificateModal from "../../common/VerificationApprovalCertificateModal/VerificationApprovalCertificateModal";
import { POST, GET } from "../../service/CRUDService";
import Lottie from "lottie-react";
import verificationAnimation from "../../images/verification.json";
import AlertIcon from "../../resources/icons/Alert.svg";
import LegacyMatchesModal from "../../common/LegacyMatchesModal/LegacyMatchesModal";
import { detectFileType } from "../../utils/DetectFileType";
import { formatDate } from "../../utils/formatDate";
import { getStablePdfUrl } from "../../utils/pdfUtils/stablePdfUrl";
import PdfPreview from "../../common/PdfPreview/PdfPreview";
import {
  formStructureBuilder,
  formatFieldName,
} from "../../utils/formStructureBuilder";

const TransactionDetailsPage = () => {
  const { transaction_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionType, setActionType] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [currentTransactionId, setCurrentTransactionId] = useState(null);
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [issuerComments, setIssuerComments] = useState("");
  const [verifierComments, setVerifierComments] = useState("");
  const [isCertificateModalOpen, setIsCertificateModalOpen] = useState(false);
  const [matchingVCs, setMatchingVCs] = useState([]);
  const [isMatchesModalOpen, setIsMatchesModalOpen] = useState(false);
  const [isMatchingVCsLoading, setIsMatchingVCsLoading] = useState(false);

  const verificationDataStructure = useMemo(() => {
    if (transactionDetails?.verification_data) {
      return formStructureBuilder(transactionDetails.verification_data);
    }
    return {};
  }, [transactionDetails?.verification_data]);

  const renderSimpleFields = (structure, labelMap = {}) => {
    const simpleFields = Object.entries(structure).filter(
      ([, config]) => config.type === "text" || config.type === "null"
    );

    return simpleFields.map(([key, config]) => (
      <div key={key} className="space-y-2">
        <span className="text-sm text-gray-500 block font-medium">
          {labelMap[key] || formatFieldName(key)}
        </span>
        <p className="text-gray-900 text-sm font-medium">
          {config.value || "N/A"}
        </p>
      </div>
    ));
  };

  const renderComplexFields = (structure, labelMap = {}) => {
    const complexFields = Object.entries(structure).filter(
      ([, config]) =>
        config.type === "arrayOfObjects" ||
        config.type === "simpleArray" ||
        config.type === "object"
    );

    return complexFields.map(([key, config]) => {
      const label = labelMap[key] || formatFieldName(key);

      if (config.type === "arrayOfObjects" && config.isArray) {
        return (
          <div key={key} className="col-span-2 space-y-3">
            <span className="text-sm text-gray-500 block font-medium">
              {label}
            </span>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto">
              {config.items.map((item, itemIndex) => (
                <div
                  key={itemIndex}
                  className="p-3 border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="space-y-2">
                    {config.fields.map((field) => (
                      <div key={field} className="flex justify-between">
                        <span className="text-xs text-gray-600 font-medium capitalize">
                          {formatFieldName(field)}:
                        </span>
                        <span className="text-xs text-gray-900 font-semibold text-right max-w-[60%] break-words">
                          {item[field] || "N/A"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      } else if (config.type === "simpleArray" && config.isArray) {
        return (
          <div key={key} className="col-span-2">
            <span className="text-sm text-gray-500 block font-medium">
              {label}
            </span>
            <div className="flex flex-wrap gap-2 mt-2">
              {config.items.map((item, itemIndex) => (
                <span
                  key={itemIndex}
                  className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        );
      } else if (config.type === "object" && !config.isArray) {
        return (
          <div key={key} className="col-span-2 space-y-3">
            <span className="text-sm text-gray-500 block font-medium">
              {label}
            </span>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
              {Object.entries(config.value).map(([nestedKey, nestedValue]) => (
                <div
                  key={nestedKey}
                  className="flex justify-between items-start"
                >
                  <span className="text-xs text-gray-600 font-medium capitalize">
                    {formatFieldName(nestedKey)}:
                  </span>
                  <span className="text-xs text-gray-900 font-semibold text-right max-w-[60%] break-words">
                    {nestedValue || "N/A"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      }
      return null;
    });
  };

  const handleAccept = () => {
    setActionType("Approved");
    setIssuerComments("");
    setIsModalOpen(true);
  };

  const handleReject = () => {
    setActionType("Rejected");
    setIssuerComments("");
    setIsModalOpen(true);
  };

  const handleBackClick = () => {
    navigate("/issuer/approvals/all", {
      state: {
        filters: location.state?.filters,
      },
    });
  };

  const handleConfirmAction = async (actionType, comment = "") => {
    const body = {
      transaction_id: transaction_id,
      verification_approval_status: actionType,
      ...(comment.trim() && { issuer_comments: comment.trim() }),
    };
    try {
      const response = await POST(
        EntityConstants.GET_VERIFICATION_APPROVALS_LIST,
        body
      );
      if (response.ok) {
        setCurrentStatus(actionType);
        Toast({
          type: "success",
          title: "Transaction Updated",
          message: `Transaction has been ${actionType.toLowerCase()} successfully!`,
        });

        // Navigate back to the list after a short delay
        setTimeout(() => {
          navigate("/issuer/approvals/all", {
            state: {
              filters: location.state?.filters,
            },
          });
        }, 1500);
      } else {
        Toast({
          type: "error",
          title: "Error",
          message: "There was an error updating the transaction status.",
        });
      }
    } catch (error) {
      Toast({
        type: "error",
        title: "Error",
        message: "An error occurred while updating the transaction status.",
      });
    }
    setIsModalOpen(false);
  };

  const handleView = () => {
    if (!transactionDetails?.uploaded_document) {
      Toast({
        type: "error",
        title: "Error",
        message: "Certificate preview not available.",
      });
      return;
    }
    setIsOpen(true);
    setCurrentTransactionId(transaction_id);
  };

  const handleViewOriginal = () => {
    if (!transactionDetails?.issued_certificate_svg_url) {
      Toast({
        type: "error",
        title: "Error",
        message: "Original certificate preview not available.",
      });
      return;
    }
    setIsCertificateModalOpen(true);
  };

  const fetchMatchingVCs = async () => {
    setIsMatchingVCsLoading(true);
    try {
      const response = await GET(
        `${EntityConstants.GET_VERIFICATION_APPROVALS_LIST}/${transaction_id}/search`
      );
      const data = await response.json();

      if (data.error) {
        Toast({ type: "error", title: "Error", message: data.error });
      } else {
        setMatchingVCs(data);
      }
    } catch (error) {
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to load matching VCs.",
      });
    } finally {
      setIsMatchingVCsLoading(false);
    }
  };

  useEffect(() => {
    const fetchTransactionDetails = async () => {
      try {
        setLoading(true);
        const response = await GET(
          `${EntityConstants.GET_VERIFICATION_APPROVALS_LIST}/${transaction_id}`
        );
        const data = await response.json();

        if (data.error) {
          Toast({ type: "error", title: "Error", message: data.error });
        } else {
          setTransactionDetails(data);

          const actualStatus =
            data.verification_approval_status ||
            data.status ||
            data.approval_status ||
            data.current_status ||
            "Approval Pending";

          setCurrentStatus(actualStatus);

          setIssuerComments(data?.issuer_comments || "");
          setVerifierComments(data.comments);

          if (data.document_type === "legacy" || data.document_type === "OCR") {
            await fetchMatchingVCs();
          }
        }
      } catch (error) {
        Toast({
          type: "error",
          title: "Error",
          message: "Failed to load transaction details.",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTransactionDetails();
  }, [transaction_id]);

  const stablePdfUrl = useMemo(() => {
    return getStablePdfUrl(transactionDetails?.uploaded_document);
  }, [transactionDetails?.uploaded_document]);

  if (loading || currentStatus === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-start justify-center">
        <div className="mt-40">
          <CfLoader />
        </div>
      </div>
    );
  }

  const handleViewMatches = () => {
    if (matchingVCs.length === 0) {
      Toast({
        type: "error",
        title: "Error",
        message: "No matching certificates found.",
      });
      return;
    }
    setIsMatchesModalOpen(true);
  };

  const isLegacy = transactionDetails?.document_type?.toLowerCase() !== "vc";

  const labelMap =
    transactionDetails?.verification_form?.components?.reduce(
      (labels, component) => {
        labels[component.key] = component.label;
        return labels;
      },
      {}
    ) || {};

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-10">
      {/* Header */}
      <div className="w-full bg-cfSearchContainer flex justify-center py-[-0.9rem] px-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between w-full gap-2 sm:gap-0 max-w-8xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
            <div className="flex flex-col sm:flex-row items-center h-[4rem] sm:items-center justify-between w-full">
              <div className="">
                <div className="flex items-center gap-2 text-cfHeadingText font-semibold text-lg self-start my-auto">
                  <h2 className="text-lg font-medium flex items-center justify-center h-[4rem] w-full">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <span
                        onClick={handleBackClick}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleBackClick();
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        className="hover:text-gray-800 cursor-pointer"
                      >
                        Verification approvals
                      </span>
                      <ChevronLeft className="h-4 w-4 text-gray-500 transform rotate-180" />
                      <span className="text-gray-800 font-medium">
                        Transaction ID: #{transaction_id}
                      </span>
                    </div>
                  </h2>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-8xl mx-auto px-4">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-4 mt-2">
          <h1 className="text-xl font-bold text-gray-900 mb-2 lg:mb-0">
            Transaction ID: #{transaction_id}
          </h1>

          {currentStatus === "Approval Pending" ? (
            <div className="flex flex-col items-start space-y-4 lg:flex-row lg:space-y-0 lg:space-x-4">
              <button
                onClick={handleAccept}
                className="flex rounded-[8px] items-center justify-center gap-2 w-full lg:w-[10rem] h-[2.5rem] bg-[#3538CD] text-white font-medium lg:font-semibold text-sm lg:text-md hover:bg-cfPrimaryDark transition duration-200 focus:outline-none"
              >
                Approve
              </button>
              <button
                onClick={handleReject}
                className="flex rounded-[8px] items-center justify-center gap-2 w-full lg:w-[10rem] h-[2.5rem] bg-white text-black font-medium lg:font-semibold text-sm lg:text-md border border-gray-800 hover:bg-gray-100 transition duration-200 focus:outline-none"
              >
                Reject
              </button>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <div
                className={`px-4 py-2 rounded-lg ${
                  currentStatus === "Approved"
                    ? "bg-cfPrimaryButton text-white"
                    : "bg-red-500 text-white"
                }`}
              >
                <span className="text-white font-medium">
                  Status:{" "}
                  {currentStatus === "Approved" ? "Approved" : "Rejected"}
                </span>
              </div>

              {issuerComments && (
                <div className="relative group">
                  <img
                    src={AlertIcon}
                    alt="Info"
                    className="w-5 h-5 cursor-pointer"
                  />
                  <div className="absolute right-[-3rem] top-8 w-72 scale-0 transform rounded-lg text-xs font-medium transition-all duration-500 group-hover:scale-100 z-50">
                    <div className="flex flex-col items-center">
                      <div className="rounded-lg px-3 py-3 text-xs border border-gray-900 bg-white text-black whitespace-normal break-words">
                        <div className="flex flex-col h-full">
                          <span className="font-bold text-[16px] text-gray-900">
                            {currentStatus === "Approved"
                              ? "Approval Comment:"
                              : "Rejection Reason:"}
                          </span>
                          <span className="mt-1 text-[14px] text-gray-600 flex-grow">
                            {issuerComments || "No comment provided"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {(transactionDetails?.vc_status === "Verified" ||
          transactionDetails?.vc_status === "Revoked") &&
          transactionDetails?.issued_certificate_svg_url && (
            <div
              className={`flex flex-col lg:flex-row items-center justify-between w-full px-4 py-3 rounded-lg font-semibold text-[24px] mt-2 mb-6 shadow-sm gap-4 ${
                transactionDetails.vc_status === "Verified"
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              <div className="w-full lg:w-2/3 flex items-center justify-center gap-3 text-center">
                {transactionDetails.vc_status === "Verified" && (
                  <div className="w-12 h-12 flex-shrink-0">
                    <Lottie
                      animationData={verificationAnimation}
                      loop={true}
                      autoplay={true}
                      style={{ width: "100%", height: "100%" }}
                    />
                  </div>
                )}
                <span className="text-[24px] font-medium lg:font-semibold">
                  {transactionDetails.vc_status === "Verified"
                    ? "This credential was successfully verified by the issuer and remains valid as of today."
                    : "This credential has been revoked by the issuer and is no longer valid."}
                </span>
              </div>

              <div className="w-full lg:w-1/3 flex justify-center lg:justify-center">
                <button
                  onClick={handleViewOriginal}
                  className="flex rounded-[8px] items-center justify-center gap-2 w-full lg:w-[10rem] h-[2.5rem] bg-white text-black font-medium lg:font-semibold text-sm lg:text-md border border-gray-800 hover:bg-gray-100 transition duration-200 focus:outline-none"
                >
                  View Original
                </button>
              </div>
            </div>
          )}

        {!loading && isLegacy && !isMatchingVCsLoading && (
          <div
            className={`flex flex-col lg:flex-row items-center justify-between w-full px-4 py-3 rounded-lg font-semibold text-[24px] mt-2 mb-6 shadow-sm gap-4 ${
              matchingVCs.length > 0
                ? "bg-green-100 text-green-800"
                : "bg-[#fef2c6] text-[#db6a02]"
            }`}
          >
            {matchingVCs.length > 0 ? (
              <>
                <div className="w-full lg:w-2/3 flex items-center justify-center lg:justify-center gap-3 text-center">
                  <div className="w-12 h-12 flex-shrink-0"></div>
                  <span className="text-[24px] font-medium lg:font-semibold">
                    {`${matchingVCs.length} match${
                      matchingVCs.length > 1 ? "es" : ""
                    } found for this certificate`}
                  </span>
                </div>
                <div className="w-full lg:w-1/3 flex justify-center lg:justify-center">
                  <button
                    onClick={handleViewMatches}
                    className="flex rounded-[8px] items-center justify-center gap-2 w-full lg:w-[10rem] h-[2.5rem] bg-white text-black font-medium lg:font-semibold text-sm lg:text-md border border-gray-800 hover:bg-gray-100 transition duration-200"
                  >
                    View Certificates
                  </button>
                </div>
              </>
            ) : (
              <div className="w-full flex items-center justify-center gap-3 text-center">
                <span className="text-[24px] font-medium lg:font-semibold">
                  No Matching certificates found!
                </span>
              </div>
            )}
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Candidate Details Card */}
            <div className="bg-white rounded-lg shadow-sm p-6 max-h-screen overflow-y-auto">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 sticky top-0 bg-white py-2">
                Candidate Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {isLegacy ? (
                  // Dynamic Legacy Fields using form structure builder
                  transactionDetails?.verification_data &&
                  Object.keys(transactionDetails.verification_data).length >
                    0 ? (
                    <>
                      {renderSimpleFields(verificationDataStructure, labelMap)}

                      {renderComplexFields(verificationDataStructure, labelMap)}
                    </>
                  ) : (
                    <p className="text-gray-500 col-span-2">
                      No candidate details available
                    </p>
                  )
                ) : (
                  // VC Fields
                  <>
                    <div className="space-y-4">
                      <div>
                        <span className="text-sm text-gray-500 block">
                          Name
                        </span>
                        <p className="text-gray-900">
                          {transactionDetails?.holder_name || "N/A"}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500 block">
                          Certificate ID
                        </span>
                        <p className="text-gray-900">
                          {transactionDetails?.certificate_id || "N/A"}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500 block">
                          Phone
                        </span>
                        <p className="text-gray-900">
                          {transactionDetails?.holder_phone_number || "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <span className="text-sm text-gray-500 block">
                          Programme Name
                        </span>
                        <p className="text-gray-900">
                          {transactionDetails?.programme_name || "N/A"}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500 block">
                          Email
                        </span>
                        <p className="text-gray-900">
                          {transactionDetails?.holder_email || "N/A"}
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Verifier Organization Details Card */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Verifier Organization Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <span className="text-sm text-gray-500 block">
                      Verifier Organization
                    </span>
                    <p className="text-gray-900">
                      {transactionDetails?.verifier_org_name || "N/A"}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 block">
                      Organization Code
                    </span>
                    <p className="text-gray-900">
                      {transactionDetails?.verifier_org_code || "N/A"}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 block">
                      Verifier Email
                    </span>
                    <p className="text-gray-900">
                      {transactionDetails?.verifier_email || "N/A"}
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <span className="text-sm text-gray-500 block">
                      Created At
                    </span>
                    <p className="text-gray-900">
                      {formatDate(transactionDetails?.created_at)}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 block">
                      Verifier Name
                    </span>
                    <p className="text-gray-900">
                      {transactionDetails?.verifier_name || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Summary Card */}
            {((transactionDetails?.verification_cost &&
              parseFloat(transactionDetails?.verification_cost) !== 0) ||
              transactionDetails?.payment_date ||
              transactionDetails?.payment_id ||
              transactionDetails?.mode_of_payment) && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Payment Summary
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <span className="text-sm text-gray-500 block">
                        Amount Paid
                      </span>
                      <p className="text-gray-900">
                        {transactionDetails?.verification_cost &&
                        parseFloat(transactionDetails?.verification_cost) !== 0
                          ? `₹ ${(
                              parseFloat(
                                transactionDetails?.verification_cost
                              ) / 100
                            ).toFixed(2)}`
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500 block">
                        Paid At
                      </span>
                      <p className="text-gray-900">
                        {transactionDetails?.payment_date
                          ? formatDate(transactionDetails?.payment_date)
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500 block">
                        Payment Status
                      </span>
                      <p className="text-gray-900">
                        {transactionDetails?.payment_status === "Done"
                          ? "Paid"
                          : transactionDetails?.payment_status ||
                            "Free of charge"}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <span className="text-sm text-gray-500 block">
                        Reference ID
                      </span>
                      <p className="text-gray-900">
                        {transactionDetails?.payment_id || "N/A"}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500 block">
                        Mode of Payment
                      </span>
                      <p className="text-gray-900">
                        {transactionDetails?.mode_of_payment || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Document Preview */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24">
              {verifierComments && (
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Verifier Comments
                  </h2>
                  <textarea
                    placeholder="Comments"
                    className="border-2 border-dashed border-gray-200 rounded-lg p-2 w-full text-sm placeholder:text-sm resize-none overflow-y-auto"
                    name="verifier_comments"
                    value={verifierComments}
                    disabled
                    rows={4}
                    style={{ maxHeight: "7.5rem" }}
                  ></textarea>
                </div>
              )}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Verification Document
                </h2>
                <div className="space-y-4">
                  <div>
                    <span className="text-sm text-gray-500 block">
                      Document Type
                    </span>
                    <p className="text-gray-900">
                      {transactionDetails?.document_type || "N/A"}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 block">
                      Verification Mode
                    </span>
                    <p className="text-gray-900">
                      {transactionDetails?.mode_of_verification || "N/A"}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 block">
                      Uploaded At
                    </span>
                    <p className="text-gray-900">
                      {formatDate(transactionDetails?.uploaded_date)}
                    </p>
                  </div>

                  <div className="mt-6">
                    <span className="text-sm text-gray-500 block mb-3">
                      Document Preview
                    </span>
                    <button
                      className="w-full aspect-square rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center mb-6 relative focus:outline-none"
                      onClick={handleView}
                      onKeyDown={handleView}
                      aria-label="View document preview"
                    >
                      {transactionDetails?.uploaded_document ? (
                        <>
                          {(() => {
                            const uploadedFileType = detectFileType(
                              transactionDetails.uploaded_document
                            );

                            if (uploadedFileType === "image") {
                              return (
                                <img
                                  src={transactionDetails.uploaded_document}
                                  alt="Document Preview"
                                  className="max-w-full max-h-full object-contain"
                                />
                              );
                            } else if (uploadedFileType === "pdf") {
                              return (
                                <div className="w-full h-full flex items-center justify-center">
                                  <PdfPreview pdfUrl={stablePdfUrl} />
                                </div>
                              );
                            } else {
                              return (
                                <p className="text-gray-400 text-center text-sm p-4">
                                  Preview not available for this file type.
                                  Click to open.
                                </p>
                              );
                            }
                          })()}
                        </>
                      ) : (
                        <p className="text-gray-400">No preview available</p>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <VerificationApprovalModal
        isModalOpen={isModalOpen}
        toggleModal={setIsModalOpen}
        actionType={actionType}
        handleConfirmAction={() =>
          handleConfirmAction(actionType, issuerComments)
        }
        issuerComments={issuerComments}
        setIssuerComments={setIssuerComments}
        transaction_id={transaction_id}
        isLegacy={isLegacy}
        matchingVCs={matchingVCs}
        vcStatus={transactionDetails?.vc_status}
      />

      {isOpen && currentTransactionId === transaction_id && (
        <VerificationApprovalCertificateModal
          isOpen={isOpen}
          documentType={transactionDetails?.document_type}
          uploadedCertificateImage={transactionDetails?.uploaded_document}
          certificateData={
            transactionDetails.issued_certificate_details &&
            Object.keys(transactionDetails.issued_certificate_details).length >
              0
              ? transactionDetails.issued_certificate_details
              : transactionDetails.verification_data
          }
          openCloseModal={() => setIsOpen(false)}
          certificateId={transactionDetails?.certificate_id}
        />
      )}

      {isCertificateModalOpen && (
        <VerificationApprovalCertificateModal
          isOpen={isCertificateModalOpen}
          documentType={transactionDetails?.document_type}
          uploadedCertificateImage={
            transactionDetails?.issued_certificate_svg_url
          }
          certificateData={
            transactionDetails.issued_certificate_details &&
            Object.keys(transactionDetails.issued_certificate_details).length >
              0
              ? transactionDetails.issued_certificate_details
              : transactionDetails.verification_data
          }
          openCloseModal={() => setIsCertificateModalOpen(false)}
          certificateId={transactionDetails?.certificate_id}
        />
      )}

      {isMatchesModalOpen && (
        <LegacyMatchesModal
          isOpen={isMatchesModalOpen}
          certificateData={matchingVCs.map((vc) => vc.svg_url)}
          openCloseModal={() => setIsMatchesModalOpen(false)}
          handleConfirmAction={handleConfirmAction}
          transactionId={transaction_id}
          matchingVCs={matchingVCs}
          currentStatus={currentStatus}
        />
      )}
    </div>
  );
};

export default TransactionDetailsPage;
